import { useState } from "react";
import { Button } from "@/ui/button";
import { useAuthV2 as useAuth } from "@/lib/authv2/store";
import { canNominateBeauty } from "./permissions";
import { useBeautyNomination } from "./api";
import { BeautyNominationDialog } from "./BeautyNominationDialog";

type Props = {
  orderId: string;
  /**
   * "card" cho thẻ đơn trên danh sách, "detail" cho dòng "Khách đẹp" trong
   * khối "Đánh giá ảnh" ở màn chi tiết (CEO chốt 2a ngày 01/10/2026 — trước đó
   * nút nằm lẫn trên hàng nút trạng thái của đơn).
   */
  variant?: "card" | "detail";
};

/**
 * Nút "Khách đẹp" — người gặp khách tận nơi nói "đơn này đẹp, marketing nên
 * xem" (CEO chốt 26/09/2026, hợp đồng khachdep.v1 §8).
 *
 * KHÔNG hiện gì với người ngoài bảy phòng gặp khách: hợp đồng §8 nói rõ "ai
 * không thuộc danh sách thì KHÔNG THẤY nút — không phải thấy rồi bấm mới báo
 * lỗi".
 *
 * Nút KHÔNG phụ thuộc công tắc gửi ở máy chủ. Công tắc tắt thì đề cử nằm
 * trong hàng chờ tại chỗ và tự đi khi Center lên; người bấm không bao giờ
 * thấy lỗi vì chuyện đó.
 */
export function BeautyNominationButton({ orderId, variant = "detail" }: Props) {
  const staff = useAuth((s) => s.currentStaff);
  const allowed = canNominateBeauty(staff);
  const [open, setOpen] = useState(false);

  // Trên THẺ ĐƠN, chỉ hỏi máy chủ khi hộp thoại đã mở.
  //
  // Danh sách đơn vẽ tới vài trăm thẻ một lúc; hỏi trạng thái cho từng thẻ là
  // vài trăm lượt gọi mạng mỗi lần mở màn Đơn hàng, để đổi lấy một ngôi sao
  // trên vài thẻ. Màn chi tiết thì hỏi ngay — ở đó chỉ có MỘT đơn, và nhãn
  // "đã đề cử" là thứ người dùng cần thấy trước khi bấm.
  const askNow = allowed && (variant === "detail" || open);
  const { data: nomination } = useBeautyNomination(orderId, askNow);

  if (!allowed) return null;

  const active = nomination?.status === "active";
  // Trong khối "Đánh giá ảnh" dòng đã mang nhãn "Khách đẹp" bên trái, nên nút
  // nói thẳng việc nó làm — đúng chữ trên ảnh mẫu CEO duyệt 01/10/2026.
  const label =
    variant === "detail"
      ? active
        ? "★ Sửa / gỡ đề cử"
        : "☆ Đề cử khách đẹp"
      : active
        ? "★ Khách đẹp"
        : "Khách đẹp";
  // Màu hồng của ảnh mẫu chỉ ở dòng trong khối; thẻ đơn giữ khuôn cũ.
  const tone =
    variant === "detail"
      ? active
        ? "border-pink-700 bg-pink-700 text-white hover:bg-pink-800"
        : "border-pink-300 text-pink-700 hover:bg-pink-50 dark:border-pink-500/50 dark:text-pink-300 dark:hover:bg-pink-500/10"
      : undefined;

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant={active && variant === "card" ? "default" : "outline"}
        className={tone}
        title={active ? "Đơn này đã đề cử cho marketing — bấm để sửa hoặc gỡ" : "Đơn này đẹp, marketing nên xem"}
        onClick={(e) => {
          // Thẻ đơn nằm trong một hàng bấm được: không chặn nổi bọt thì bấm
          // nút cũng mở luôn màn chi tiết đơn ở dưới.
          e.stopPropagation();
          e.preventDefault();
          setOpen(true);
        }}
      >
        {label}
      </Button>
      <BeautyNominationDialog
        orderId={orderId}
        current={nomination ?? null}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  );
}
